// 보기: tensor's four default views (design 02 3.6), computed on this device
// from the rows (views.ts): 분류별 (예산 대비), 월별 추이, 사람별, 결제수단별,
// and 예측 (forecast.ts, design 02 12.2): each category's next 6 months;
// 저장한 보기: the workbook's saved views (SavedViews.tsx, /views/<id>).
import { useMemo, useState } from 'react'

import { readPref, writePref } from '../../lib/prefs'
import { Spinner } from '../../ui/common'
import { chartShapes } from '../chart'
import { type ForecastLine, forecast } from '../forecast'
import { monthOf, monthTitle, num, shortMonth, won } from '../format'
import { type CategoryView, type MonthLine, byCategory, byDimension, trend } from '../views'
import { Bar, type Current, MonthSwitch } from './common'
import { SavedViewList } from './SavedViews'

const VIEWS = [
  { value: 'category', label: '분류별' },
  { value: 'trend', label: '월별 추이' },
  { value: 'person', label: '사람별' },
  { value: 'payment', label: '결제수단별' },
  { value: 'forecast', label: '예측' },
  { value: 'saved', label: '저장한 보기' },
] as const

type ViewName = (typeof VIEWS)[number]['value']
const VIEW_PREF = 'tensor.view'

/** Picks the tab the 보기 screen opens with (a link: /w/<id>?view=category). */
export function pickViewTab(tab: string): boolean {
  if (!VIEWS.some((v) => v.value === tab)) return false
  writePref(VIEW_PREF, tab)
  return true
}

/** The month's spending per category against the budget. */
export function CategoryBars({ view }: { view: CategoryView }) {
  if (view.lines.length === 0) return <p className="muted empty-note">이 달에는 지출도 예산도 없습니다.</p>
  return (
    <ul className="list t-bars">
      {view.lines.map((l) => (
        <li key={l.id} className="t-bar-line">
          <div className="t-bar-head">
            <span className="t-bar-name">{l.name}</span>
            <span className="t-bar-amount">
              {won(l.spent)}
              {l.budget > 0 && <span className="muted"> / {won(l.budget)}</span>}
            </span>
          </div>
          <Bar part={l.spent} whole={l.budget} label={`${l.name} 예산 대비`} />
          <div className="t-bar-foot small">
            {l.budget <= 0 ? (
              <span className="muted">예산 없음</span>
            ) : l.left >= 0 ? (
              <span className="muted">{won(l.left)} 남음</span>
            ) : (
              <span className="danger">{won(-l.left)} 넘음</span>
            )}
          </div>
          {l.children.length > 0 && (
            <ul className="t-bar-children">
              {l.children.map((c) => (
                <li key={c.id}>
                  <span>{c.name}</span>
                  <span>
                    {won(c.spent)}
                    {c.budget > 0 && <span className="muted"> / {won(c.budget)}</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  )
}

/** Income and expense of each month as pairs of bars (drawn as SVG). */
function TrendChart({ lines }: { lines: MonthLine[] }) {
  const max = Math.max(1, ...lines.map((l) => Math.max(l.income, l.expense)))
  const W = 60
  const H = 140
  const year = lines[lines.length - 1]?.month.slice(0, 4)
  return (
    <svg
      className="t-chart"
      viewBox={`0 0 ${lines.length * W} ${H + 22}`}
      role="img"
      aria-label="월별 수입과 지출 막대그림"
    >
      {lines.map((l, i) => {
        const hi = (l.income / max) * H
        const he = (l.expense / max) * H
        const x = i * W
        return (
          <g key={l.month}>
            <rect className="t-chart-income" x={x + 12} y={H - hi} width={17} height={hi} rx={2}>
              <title>{`${monthTitle(l.month)} 수입 ${won(l.income)}`}</title>
            </rect>
            <rect className="t-chart-expense" x={x + 31} y={H - he} width={17} height={he} rx={2}>
              <title>{`${monthTitle(l.month)} 지출 ${won(l.expense)}`}</title>
            </rect>
            <text x={x + W / 2} y={H + 16} textAnchor="middle" className="t-chart-label">
              {shortMonth(l.month, year)}
            </text>
          </g>
        )
      })}
      <line x1={0} x2={lines.length * W} y1={H + 0.5} y2={H + 0.5} className="t-chart-axis" />
    </svg>
  )
}

function TrendView({ cur, month }: { cur: Current; month: string }) {
  const lines = useMemo(() => trend(cur.rows, month, 12), [cur.rows, month])
  return (
    <>
      <div className="card">
        <TrendChart lines={lines} />
        <p className="t-legend small">
          <span className="t-key income" /> 수입 <span className="t-key expense" /> 지출
        </p>
      </div>
      <table className="t-table">
        <thead>
          <tr>
            <th>달</th>
            <th className="num">수입</th>
            <th className="num">지출</th>
            <th className="num">남은 돈</th>
          </tr>
        </thead>
        <tbody>
          {[...lines].reverse().map((l) => (
            <tr key={l.month}>
              <td>{monthTitle(l.month)}</td>
              <td className="num income">{num(l.income)}</td>
              <td className="num">{num(l.expense)}</td>
              <td className={`num${l.balance < 0 ? ' danger' : ''}`}>{num(l.balance)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

function DimensionView({ cur, month, role }: { cur: Current; month: string; role: 'person' | 'payment' }) {
  const items = cur.book?.items ?? {}
  const lines = useMemo(() => byDimension(cur.rows, items, role, month), [cur.rows, items, role, month])
  const max = Math.max(0, ...lines.map((l) => l.expense))
  if (lines.length === 0) return <p className="muted empty-note">이 달에 적은 거래가 없습니다.</p>
  return (
    <table className="t-table t-dim">
      <thead>
        <tr>
          <th>{role === 'person' ? '사람' : '결제수단'}</th>
          <th className="num">지출</th>
          <th className="num">수입</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((l) => (
          <tr key={l.id}>
            <td>
              {l.name}
              <Bar part={l.expense} whole={max} />
            </td>
            <td className="num">{num(l.expense)}</td>
            <td className="num income">{num(l.income)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** The total's last months and the forecast as one line chart. */
function ForecastChart({ line }: { line: ForecastLine }) {
  const hist = line.history.filter((p, i) => p.amount > 0 || line.history.slice(0, i).some((q) => q.amount > 0))
  const labels = [...hist, ...line.forecast].map((p) => shortMonth(p.month))
  const data = {
    labels,
    series: [
      { name: '지난 달', values: [...hist.map((p) => p.amount), ...line.forecast.map(() => null)] },
      {
        name: '예측',
        values: [...hist.map((p, i) => (i === hist.length - 1 ? p.amount : null)), ...line.forecast.map((p) => p.amount)],
      },
    ],
  }
  const W = 640
  const H = 220
  return (
    <svg className="t-chart t-forecast-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="지출 추세와 예측">
      {chartShapes('line', data, W, H).map((sh, i) => {
        const a = sh.attrs as Record<string, string | number>
        if (sh.kind === 'text')
          return (
            <text key={i} {...a}>
              {sh.text}
            </text>
          )
        if (sh.kind === 'rect') return <rect key={i} {...a} />
        if (sh.kind === 'line') return <line key={i} {...a} />
        if (sh.kind === 'circle') return <circle key={i} {...a} />
        return <path key={i} {...a} />
      })}
    </svg>
  )
}

function slopeText(n: number): string {
  if (n === 0) return '변화 없음'
  return `월 ${n > 0 ? '+' : '-'}${num(Math.abs(n))}원`
}

function ForecastView({ cur }: { cur: Current }) {
  const items = cur.book?.items ?? {}
  const from = monthOf(Date.now())
  const f = useMemo(() => forecast(cur.rows, items, from), [cur.rows, items, from])
  if (!f.ready)
    return (
      <p className="muted empty-note">
        3개월 이상 쌓이면 예측합니다. 지금은 지난달까지 {f.used}개월입니다(이번 달은 다 지나야 셉니다).
      </p>
    )
  const months = f.total.forecast.map((p) => p.month)
  return (
    <>
      <div className="card">
        <ForecastChart line={f.total} />
        <p className="muted small">
          이번 달 전까지의 다 지난 달 {f.used}개월(최대 12개월)에 맞춘 직선으로 계산했습니다(칸의 predtrend와 같은 계산). 이번 달은 아직 다
          지나지 않아 쓰지 않습니다.
        </p>
        {[...f.lines, f.total].some((l) => l.clamped) && (
          <p className="muted small">0보다 작게 나온 예측은 0으로 보입니다.</p>
        )}
      </div>
      <div className="t-table-wrap">
        <table className="t-table t-forecast">
          <thead>
            <tr>
              <th>분류</th>
              <th className="num">추세</th>
              {months.map((m) => (
                <th key={m} className="num">
                  {shortMonth(m)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...f.lines, f.total].map((l) => (
              <tr key={l === f.total ? 'total' : l.id} className={l === f.total ? 't-total-row' : undefined}>
                <td>{l.name}</td>
                <td className={`num${l.slope > 0 ? ' danger' : ''}`}>{slopeText(l.slope)}</td>
                {l.forecast.map((p) => (
                  <td key={p.month} className="num">
                    {num(p.amount)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

export function ViewsScreen({ cur, month, setMonth }: { cur: Current; month: string; setMonth: (m: string) => void }) {
  const [view, setView] = useState<ViewName>(() => readPref(VIEW_PREF, VIEWS.map((v) => v.value), 'category'))
  const items = cur.book?.items ?? {}
  const budgets = cur.book?.budgets ?? {}
  const cat = useMemo(() => byCategory(cur.rows, items, budgets, month), [cur.rows, items, budgets, month])
  return (
    <div className="page t-page t-wide-page">
      <div className="tabs t-view-tabs" role="tablist" aria-label="보기">
        {VIEWS.map((v) => (
          <button
            key={v.value}
            type="button"
            role="tab"
            aria-selected={view === v.value}
            onClick={() => {
              setView(v.value)
              writePref(VIEW_PREF, v.value)
            }}
          >
            {v.label}
          </button>
        ))}
      </div>
      {view !== 'forecast' && view !== 'saved' && <MonthSwitch month={month} onChange={setMonth} />}
      {cur.book?.loading && cur.rows.length === 0 && <Spinner />}
      {view === 'category' && (
        <>
          <p className="t-total-line">
            지출 <strong>{won(cat.spent)}</strong>
            {cat.budget > 0 && (
              <>
                {' '}
                / 예산 <strong>{won(cat.budget)}</strong>
              </>
            )}
          </p>
          <CategoryBars view={cat} />
        </>
      )}
      {view === 'trend' && <TrendView cur={cur} month={month} />}
      {view === 'person' && <DimensionView cur={cur} month={month} role="person" />}
      {view === 'payment' && <DimensionView cur={cur} month={month} role="payment" />}
      {view === 'forecast' && <ForecastView cur={cur} />}
      {view === 'saved' && <SavedViewList cur={cur} />}
    </div>
  )
}
